/**
 * Document Generator Component
 *
 * Quick document generation with smart-fill from context (vehicle, customer, deal).
 * Redesigned for world-class standards with interactive form support.
 */

import {
  ArrowDownTrayIcon,
  CircleStackIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  PrinterIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import DocumentFormModal from './DocumentFormModal';

interface DocumentGeneratorProps {
  vehicleId?: string;
  customerId?: string;
  customerEmail?: string;
  dealId?: string;
  compact?: boolean;
}

interface SavedCustomerDocument {
  id: string;
  template_id: string;
  template_name: string;
  file_name: string;
  created_at: string;
  emailed_at?: string | null;
  email_to?: string | null;
}

interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  category: string;
  required_fields: string[];
}

type TemplatesResponse = { templates: TemplateMetadata[] } | TemplateMetadata[];

const DocumentGenerator: React.FC<DocumentGeneratorProps> = ({
  vehicleId,
  customerId,
  customerEmail,
  dealId: _dealId,
  compact = false,
}) => {
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('bill_of_sale');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedDocs, setSavedDocs] = useState<SavedCustomerDocument[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingSavedDocs, setLoadingSavedDocs] = useState(false);
  const [initialData, setInitialData] = useState<Record<string, string>>({});
  const [loadingInitialData, setLoadingInitialData] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await apiService.get<TemplatesResponse>('/documents/templates');
        const nextTemplates: TemplateMetadata[] = Array.isArray(response)
          ? response
          : Array.isArray((response as any)?.templates)
            ? (response as any).templates
            : [];

        setTemplates(nextTemplates);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    if (templates.length === 0) return;
    const hasSelected = templates.some((t) => t.id === selectedTemplateId);
    if (!hasSelected) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId),
    [selectedTemplateId, templates]
  );

  const fetchSavedDocs = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoadingSavedDocs(true);
      const response = await apiService.get<{ documents: SavedCustomerDocument[] }>(
        `/documents/customers/${customerId}`
      );
      setSavedDocs(response.documents || []);
    } catch (error: any) {
      console.error('Failed to fetch saved documents:', error);
    } finally {
      setLoadingSavedDocs(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      fetchSavedDocs();
    }
  }, [customerId, fetchSavedDocs]);

  const handleGenerateClick = async () => {
    if (!selectedTemplateId) {
      return;
    }

    setLoadingInitialData(true);
    try {
      const response = await apiService.post<{
        data?: Record<string, any>;
      }>('/documents/preview', {
        template_id: selectedTemplateId,
        vehicle_id: vehicleId || null,
        customer_id: customerId || null,
        custom_data: {},
      });

      const previewData = response?.data || {};
      const normalized: Record<string, string> = {};
      Object.entries(previewData).forEach(([key, value]) => {
        normalized[key] = value === null || value === undefined ? '' : String(value);
      });
      setInitialData(normalized);
    } catch (error) {
      console.error('Failed to load preview data:', error);
      setInitialData({});
    } finally {
      setLoadingInitialData(false);
      setIsModalOpen(true);
    }
  };

  const handleFormSubmit = async (
    formData: Record<string, string>,
    action: 'download' | 'print' | 'email' = 'download',
    emailTo?: string
  ) => {
    setGenerating(true);
    try {
      if (action === 'email') {
        if (!customerId) {
          toast.error('Select a customer before sending document by email.');
          return;
        }

        const saveResponse = await apiService.post<{
          document_id: string;
          file_name: string;
        }>('/documents/generate-and-save', {
          template_id: selectedTemplateId,
          vehicle_id: vehicleId || null,
          customer_id: customerId,
          custom_data: formData,
        });

        const toEmail = (emailTo || customerEmail || '').trim();
        if (!toEmail) {
          toast.error('Recipient email is required to send document.');
          return;
        }

        await apiService.post(`/documents/${saveResponse.document_id}/email`, {
          to_email: toEmail,
          subject: selectedTemplate?.name || 'Document',
          body: 'Please see the attached document.',
        });

        await fetchSavedDocs();
        toast.success('Document generated and emailed successfully');
      } else {
        const response = await apiService.post(
          '/documents/generate',
          {
            template_id: selectedTemplateId,
            vehicle_id: vehicleId || null,
            customer_id: customerId || null,
            custom_data: formData,
          },
          {
            responseType: 'blob',
          }
        );

        const blob = new Blob([response as any], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (action === 'print') {
          const printWindow = window.open(url, '_blank');
          if (printWindow) {
            printWindow.addEventListener('load', () => {
              printWindow.print();
            });
          }
          toast.success('Print preview opened');
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `${selectedTemplate?.name || 'document'}_${Date.now()}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success('Document generated successfully');
        }

        window.URL.revokeObjectURL(url);
      }

      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Failed to generate document:', error);
      toast.error('Failed to process document');
    } finally {
      setGenerating(false);
    }
  };

  if (compact) {
    return (
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-grow">
            <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
              Select Document
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="block w-full rounded-lg border-gray-200 bg-gray-50/50 text-sm focus:border-blue-500 focus:ring-blue-500 transition-all"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleGenerateClick}
            disabled={generating}
            className="flex items-center justify-center h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm shadow-blue-200"
          >
            {generating ? (
              <CircleStackIcon className="w-5 h-5 animate-spin" />
            ) : (
              <DocumentTextIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {selectedTemplate && (
          <DocumentFormModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleFormSubmit}
            template={selectedTemplate}
            initialData={initialData}
            vehicleId={vehicleId}
            customerId={customerId}
            generating={generating || loadingInitialData}
            showEmailOption={Boolean(customerId)}
            defaultEmailTo={customerEmail}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <DocumentTextIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Quick Documents</h3>
            <p className="text-[11px] text-gray-500">Generate & print forms instantly</p>
          </div>
        </div>
        <button
          onClick={handleGenerateClick}
          disabled={generating || !selectedTemplateId}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm shadow-blue-100"
        >
          {generating ? (
            <CircleStackIcon className="w-4 h-4 animate-spin" />
          ) : (
            <PrinterIcon className="w-4 h-4" />
          )}
          Generate
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Template Selection */}
        <div className="grid grid-cols-1 gap-4">
          {loadingTemplates ? (
            <div className="h-20 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                onClick={() => setSelectedTemplateId(template.id)}
                className={`relative flex items-start gap-4 p-4 rounded-xl border transition-all text-left ${
                  selectedTemplateId === template.id
                    ? 'border-blue-200 bg-blue-50/30'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50'
                }`}
              >
                <div
                  className={`mt-0.5 p-2 rounded-lg ${
                    selectedTemplateId === template.id
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <DocumentTextIcon className="w-5 h-5" />
                </div>
                <div className="flex-grow min-w-0">
                  <h4
                    className={`text-sm font-medium truncate ${
                      selectedTemplateId === template.id ? 'text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    {template.name}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                    {template.description}
                  </p>
                </div>
                {selectedTemplateId === template.id && (
                  <div className="absolute top-4 right-4 h-2 w-2 bg-blue-600 rounded-full"></div>
                )}
              </button>
            ))
          )}
        </div>

        {/* Recent Documents */}
        {customerId && (
          <div className="pt-4 border-t border-gray-100">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-4">
              Saved Documents
            </h4>
            <div className="space-y-3">
              {loadingSavedDocs ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-12 bg-gray-50 animate-pulse rounded-lg"></div>
                  ))}
                </div>
              ) : savedDocs.length === 0 ? (
                <div className="py-8 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                  <div className="p-3 inline-flex bg-white rounded-full border border-gray-100 mb-2">
                    <CircleStackIcon className="w-5 h-5 text-gray-300" />
                  </div>
                  <p className="text-xs text-gray-400">No documents saved for this customer</p>
                </div>
              ) : (
                savedDocs.slice(0, 3).map((doc) => (
                  <div
                    key={doc.id}
                    className="group flex items-center justify-between p-3 rounded-xl bg-gray-50/50 border border-transparent hover:bg-white hover:border-gray-100 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <DocumentArrowUpIcon className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {doc.template_name}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {savedDocs.length > 3 && (
              <button className="w-full mt-4 py-2 text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors">
                View All Documents
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="px-6 py-3 bg-gray-50/50 flex items-center gap-2">
        <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-[10px] text-gray-500">
          Smart-fill is active based on current selection
        </span>
      </div>

      {selectedTemplate && (
        <DocumentFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
          template={selectedTemplate}
          initialData={initialData}
          vehicleId={vehicleId}
          customerId={customerId}
          generating={generating || loadingInitialData}
          showEmailOption={Boolean(customerId)}
          defaultEmailTo={customerEmail}
        />
      )}
    </div>
  );
};

export default DocumentGenerator;
